import type { Dispatch, SetStateAction } from "react"

import type { LoanProduct, Proposal, ProposalCost, User } from "~/graphql/client"
import {
  MAX_PROPOSAL_MARGIN,
  MIN_PROPOSAL_MARGIN,
} from "~/utils/finance/constants"
import { PricingSummaryLineItemType } from "~/utils/finance/pricing-summary"
import type { SystemPricingItem } from "~/utils/finance/pricing-summary"

type MandatoryMarginProps = {
  marginFixed: boolean
  proposalMargin: number
  managerMarginAdjustment: boolean
  managerMinimumMargin: number
}

type EnterpriseSettings = {
  id: string
  createdAt: Date
  updatedAt: Date
  marginFixed: boolean
  proposalMargin: number
  managerMarginAdjustment: boolean
  managerMinimumMargin: number
  enterpriseId: string
  proposalId: string
}

const validateEnterpriseMarginChange = (input: {
  settings: Partial<EnterpriseSettings> & Required<MandatoryMarginProps>
  newMargin: number
  permissions: string[]
}): string | undefined => {
  const { settings, newMargin, permissions } = input

  const noMarginLimit = permissions.includes(`has_no_enterprise_margin_limit`)
  const lowerMarginLimit = permissions.includes(
    `may_use_lower_enterprise_margin_limit`,
  )

  if (newMargin > MAX_PROPOSAL_MARGIN) {
    return `Invalid margin (must be less than maximum margin)`
  }
  if (newMargin < MIN_PROPOSAL_MARGIN) {
    return `Invalid margin (must be greater than minimum margin)`
  }
  if (noMarginLimit) return undefined

  if (lowerMarginLimit) {
    if (!settings.marginFixed && newMargin > settings.proposalMargin) {
      return undefined
    }
    if (newMargin < settings.proposalMargin) {
      if (!settings.managerMarginAdjustment) {
        return `Invalid margin (must be greater than ${settings.proposalMargin})`
      } else if (newMargin < settings.managerMinimumMargin) {
        return `Invalid margin (must be greater than ${settings.managerMinimumMargin})`
      }
    }

    return undefined
  }
  if (settings.marginFixed) {
    return `Invalid margin (margin is fixed for this proposal)`
  } else if (newMargin < settings.proposalMargin) {
    return `Invalid margin (must be greater than ${settings.proposalMargin})`
  }

  return undefined
}

export type PricePerWatt = number | null

export const formatLoans = (
  items: SystemPricingItem[],
  proposal: Proposal,
  showPricing: boolean,
): SystemPricingItem[] => {
  const loanProducts: Record<string, LoanProduct> = {}

  if (proposal?.selectedLoanProduct) {
    loanProducts[proposal.selectedLoanProduct.id] = proposal.selectedLoanProduct
  }

  items.forEach((item) => {
    if (
      (item.type === PricingSummaryLineItemType.DEALER_FEE ||
        item.type === PricingSummaryLineItemType.POWUR_LENDER_MARGIN_FEE) &&
      loanProducts[item.id] &&
      !item.name.includes(`provider-fee`)
    ) {
      item.name =
        item.name +
        `<span class='provider-fee'>( ` +
        loanProducts[item.id].powurOsDealerFeePlusMargin +
        ` % )</span>`
    }
    if (item.type === `loan` && !showPricing) {
      item.total = `n/a`
      item.price = `n/a`
    }
  })

  return items
}

export const pricingItemIsEquipment = (item: { type: string }): boolean => {
  return (
    item.type === PricingSummaryLineItemType.EQUIPMENT ||
    item.type === PricingSummaryLineItemType.MODULE ||
    item.type === PricingSummaryLineItemType.INVERTER ||
    item.type === PricingSummaryLineItemType.ADDITIONAL_EQUIPMENT ||
    item.type === PricingSummaryLineItemType.ENERGY_BACKUP ||
    item.type === PricingSummaryLineItemType.EV_CHARGER ||
    item.type === PricingSummaryLineItemType.EV_OUTLET ||
    item.type === PricingSummaryLineItemType.OPTIMIZER
  )
}

export const formatBaseCostEquipment = (
  items: SystemPricingItem[],
  proposal: Proposal,
  showPricing: boolean,
): SystemPricingItem[] => {
  return items.filter((item) => pricingItemIsEquipment(item))
}

export const setNewMarginFromPricePerWatt = (
  value: PricePerWatt,
  remoteProposalCost: ProposalCost,
  setUserAdjustablePricePerWatt: Dispatch<SetStateAction<PricePerWatt>>,
  proposal: Proposal,
  systemSizeInWatts: number,
  remoteMargin: number,
  minimumMargin: number,
  currentPermissions: User[`permissions`],
  setMarginError: Dispatch<SetStateAction<string>>,
  changeLocal: (update: Partial<Proposal>) => void,
): void => {
  const userAdjustedPricePerWatt = value ? value : 0
  const totalCostWithoutMargin =
    remoteProposalCost.totalCost - remoteMargin * systemSizeInWatts
  const minAllowedTotalPerWatt =
    (totalCostWithoutMargin + systemSizeInWatts * minimumMargin) /
    systemSizeInWatts
  const maxAllowedTotalPerWatt =
    (totalCostWithoutMargin + systemSizeInWatts * 2) / systemSizeInWatts

  const priceIsTooLow = userAdjustedPricePerWatt < minAllowedTotalPerWatt
  const priceIsTooHigh = userAdjustedPricePerWatt > maxAllowedTotalPerWatt
  let constrainedAdjustedPrice = userAdjustedPricePerWatt

  if (priceIsTooLow) {
    constrainedAdjustedPrice =
      Math.round(minAllowedTotalPerWatt * 1000000 + 1) / 1000000
  } else if (priceIsTooHigh) {
    constrainedAdjustedPrice =
      Math.round(maxAllowedTotalPerWatt * 1000000 - 1) / 1000000
  }

  setUserAdjustablePricePerWatt(constrainedAdjustedPrice)

  // calc new margin per watt
  const adjustedTotalCost = constrainedAdjustedPrice * systemSizeInWatts
  const proposalMarginCash = remoteMargin * systemSizeInWatts
  const adjustedMarginCash =
    adjustedTotalCost - remoteProposalCost.totalCost + proposalMarginCash
  const newMarginPerWatt = adjustedMarginCash / systemSizeInWatts

  // validateEnterpriseMarginChange can be used to return specific error messages
  const error = proposal.enterpriseSettings
    ? validateEnterpriseMarginChange({
        settings: proposal.enterpriseSettings,
        newMargin: newMarginPerWatt,
        permissions: currentPermissions,
      })
    : undefined

  setMarginError(error ?? ``)
  if (error) {
    console.warn(error)
  } else if (!error) {
    changeLocal({ margin: newMarginPerWatt })
  }
}

export const setNewTotalCostFromPricePerWatt = (
  newPricePerWatt: number,
  remotePricePerWatt: number,
  remoteProposalCost: ProposalCost,
  proposal: Proposal,
  changeLocal: (update: Partial<Proposal>) => void,
): void => {
  const updatedTotalCost =
    (newPricePerWatt / remotePricePerWatt) * remoteProposalCost.totalCost

  changeLocal({
    proposalCost: {
      ...proposal.proposalCost,
      totalCost: updatedTotalCost,
    },
  })
}

export const isMarginWithinAcceptableRange = (margin: number): boolean => {
  return margin >= MIN_PROPOSAL_MARGIN && margin <= MAX_PROPOSAL_MARGIN
}

export const formatters = {
  currency: (amount: number, fraction = 2): string =>
    new Intl.NumberFormat(`en-US`, {
      style: `currency`,
      currency: `USD`,
      minimumFractionDigits: fraction,
    }).format(amount),

  pricePerWatt: (price: number): string => `$${price.toFixed(2)}`,

  /**
   * @deprecated Use formatMarginForDisplay for display purposes only.
   * Do NOT use for state initialization or calculations as it loses precision.
   * Kept for backward compatibility with existing code.
   */
  roundMargin: (margin: number): number => Math.round(margin * 100) / 100,

  /**
   * Formats margin for display purposes only (2 decimal places).
   * Use this ONLY for rendering, NOT for state or calculations.
   * Preserves full precision in state, formats only for display.
   */
  formatMarginForDisplay: (margin: number): string => {
    return margin.toFixed(2)
  },

  percentage: (value: number, fraction = 2): string =>
    new Intl.NumberFormat(`en-US`, {
      style: `percent`,
      minimumFractionDigits: fraction,
      maximumFractionDigits: fraction,
    }).format(value),
}

export const validateMarginCosts = (
  newMargin: number,
  minimumMargin: number,
  maxMargin: number,
): string | null => {
  if (newMargin < minimumMargin) {
    return `Margin too low; Must exceed $${minimumMargin}`
  }

  if (newMargin > maxMargin) {
    return `Margin too high; Must not exceed $${maxMargin}`
  }

  return null
}
